import { useState } from "react";
import { motion } from "framer-motion";
import { AlertTriangle, Upload, Camera } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

const ImageTest = () => {
  const [uploadMode, setUploadMode] = useState<'file' | 'camera' | null>(null);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    setIsUploading(true);
    
    // Create preview URL
    const reader = new FileReader();
    reader.onload = (e) => {
      setUploadedImage(e.target?.result as string);
      setIsUploading(false);
    };
    reader.readAsDataURL(file);

    // TODO: Upload to Supabase storage
    console.log('File uploaded:', file.name);
  };

  const handleCameraCapture = (imageDataUrl: string) => {
    setUploadedImage(imageDataUrl);
    setUploadMode(null);
  };

  const resetUpload = () => {
    setUploadedImage(null);
    setUploadMode(null);
  };

  // Simple camera component inline
  const CameraCapture = ({ onCapture: _onCapture, onCancel }: { onCapture: (url: string) => void; onCancel: () => void }) => {
    return (
      <div className="p-4 border rounded-lg">
        <p className="text-muted-foreground mb-4">Camera capture functionality</p>
        <div className="flex gap-2">
          <button onClick={onCancel} className="px-4 py-2 border rounded">Cancel</button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 py-8 max-w-4xl pt-24">
        {/* Warning Banner */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-8"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-destructive mb-1">Important Notice</h3>
              <p className="text-sm text-destructive/80">
                Please make sure you have closed the booth. Failure to do so means you will be charged for the time that door is open, or pod being used by anyone else.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Image Upload Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-card rounded-lg border p-6 mb-8"
        >
          <h2 className="text-2xl font-semibold mb-4">Submit Pod Closure Verification</h2>
          
          {!uploadedImage ? (
            <div className="space-y-4">
              <p className="text-muted-foreground mb-6">
                Take a photo or upload an image showing the pod is properly closed.
              </p>
              
              {uploadMode === 'camera' ? (
                <CameraCapture
                  onCapture={handleCameraCapture}
                  onCancel={() => setUploadMode(null)}
                />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Camera Option */}
                  <button
                    onClick={() => setUploadMode('camera')}
                    className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-muted-foreground/25 rounded-lg hover:border-primary/50 hover:bg-muted/50 transition-colors"
                  >
                    <Camera className="h-12 w-12 text-muted-foreground mb-3" />
                    <span className="font-medium">Take Photo</span>
                    <span className="text-sm text-muted-foreground mt-1">Use camera</span>
                  </button>

                  {/* File Upload Option */}
                  <label className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-muted-foreground/25 rounded-lg hover:border-primary/50 hover:bg-muted/50 transition-colors cursor-pointer">
                    <Upload className="h-12 w-12 text-muted-foreground mb-3" />
                    <span className="font-medium">Upload Image</span>
                    <span className="text-sm text-muted-foreground mt-1">Select from device</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      className="hidden"
                      disabled={isUploading}
                    />
                  </label>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="relative">
                <img
                  src={uploadedImage}
                  alt="Pod closure verification"
                  className="w-full max-w-md mx-auto rounded-lg border"
                />
              </div>
              <div className="flex justify-center gap-3">
                <button
                  onClick={resetUpload}
                  className="px-4 py-2 border border-muted-foreground/25 rounded-md hover:bg-muted transition-colors"
                >
                  Retake/Reselect
                </button>
                <button
                  onClick={() => {
                    console.log('Submitting image verification');
                    alert('Image submitted successfully!');
                  }}
                  className="px-6 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                >
                  Submit Verification
                </button>
              </div>
            </div>
          )}
        </motion.div>

        {/* Admin Status Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-card rounded-lg border p-6"
        >
          <h3 className="font-semibold mb-4">Pod Lock Status</h3>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-success animate-pulse" />
            <span className="text-muted-foreground">Pod X is locked</span>
          </div>
          <p className="text-sm text-muted-foreground mt-2">SEAM API integration placeholder</p>
        </motion.div>
      </main>

      <Footer />
    </div>
  );
};

export default ImageTest;
